'use client'
import IconUse from '_/ui/icons/use'
import { InfoContainer } from '_/ui/sections-trip/info-container'
import { Info, InfoName } from '_/ui/sections-trip/label-and-info'
import { Title } from '_/ui/title'
import Image from 'next/image'
import { tv, type VariantProps } from 'tailwind-variants'
import { type TripType } from '_/services/trips/get-trip.schema'
import { ViewPDF } from './view-pdf'
import { getIcon } from './get-icon'
import { returnFlexibleFlag } from '_/utils/return-flexible-flag'
import { convertDateFormat, convertDateFormatAndTz } from '_/utils/date'

const styles = tv(
	{
		slots: {
			base: 'w-full',
			header: 'mb-6',
			hash: 'text-primary-neutral',
			image: 'w-full object-cover h-full',
			figure: 'mb-6 h-[17.75rem] lg:h-[18.75rem]',
			list: 'grid',
			divider: 'border-b border-primary-light my-6 w-full',
			footer: 'flex justify-between',
			costLabel: 'text-sm text-gray-500',
			cost: 'text-2xl text-gray-700 font-bold ml-1.5',
			costWrapper: 'flex items-center max-md:justify-center max-md:flex-col',
			costWrapperInner: 'flex items-center',
			pdf: 'text-primary-neutral font-bold underline flex items-center gap-x-3.5',
			info: 'md:items-center',
		},
		variants: {
			size: {
				sm: {
					hash: 'mt-3 block',
					figure: '-mx-8',
					list: 'gap-6',
					footer: 'items-center flex-col',
					pdf: 'text-base',
				},
				md: {
					hash: 'ml-3.5 mt-0 inline',
					image: 'rounded-lg',
					figure: 'mx-0',
					list: 'grid-cols-asymmetric-3 gap-y-6 gap-x-5',
					footer: 'items-start flex-row',
					pdf: 'text-lg',
				},
			},
		},
	},
	{
		responsiveVariants: ['sm', 'md'],
	},
)

export interface TravelInfoProps extends VariantProps<typeof styles> {
	className?: string
	type: 'upcoming' | 'previous' | 'quote'
	trip: TripType
}

const {
	base,
	list,
	divider,
	hash,
	image,
	header,
	figure,
	footer,
	costLabel,
	cost,
	costWrapper,
	costWrapperInner,
	info,
} = styles({
	size: {
		initial: 'sm',
		md: 'md',
	},
})

const InfoDate = ({ label, date }: { date: string; label: string }) => (
	<>
		<InfoName>{label}</InfoName>
		<Info className={info()}>{date}</Info>
	</>
)

export const TravelInfo = ({ className, size, type, trip, ...rest }: TravelInfoProps) => {
	const resortDays = trip.resortDays
	const parkDays = trip.parkDays || null
	const membersWithAnnualPass = trip?.membersWithAnnualPass || null
	const destination = trip.destination
	const themes = destination.themes
	const ship = trip.ship || null
	const ships = trip.ships?.length ? trip.ships : null
	const initialDate = trip.initialDate || trip.expectedInitialDate || null
	const initialDateFlexible = trip.initialDateFlexible
	const finalDateFlexible = trip.finalDateFlexible
	const finalDate = trip.finalDate || trip.expectedFinalDate || null
	const resortCategory = trip.resortCategory?.title || trip.resortCategories[0]?.title || null
	const resortPreference = trip.resortPreference?.title || trip.resortPreferences[0]?.title || null
	const investment = trip.investment || null
	const hashCode = trip.hash || null
	const partnerConfirmation = trip.partnerConfirmation || null
	const famviaConfirmation = trip.famviaConfirmation || null
	const quoteAndCruise = themes.slug === 'cruises' && type === 'quote'

	const quoteArrivalDate = initialDate ? convertDateFormat(initialDate as unknown as string, 'MM/DD/YYYY') : null
	const quoteDepartingDate = finalDate ? convertDateFormat(finalDate as unknown as string, 'MM/DD/YYYY') : null

	const { arrivalDate: arrivalDateQuote, departingDate: departingDateQuote } = returnFlexibleFlag(
		quoteArrivalDate,
		initialDateFlexible as boolean,
		quoteDepartingDate,
		finalDateFlexible as boolean,
	)

	return (
		<article className={`${base()} ${className}`} {...rest}>
			<header className={header()}>
				<Title size='large' className='max-md:text-2xl'>
					<span className='text-gray-500'>{destination.title}</span>
					<span className={hash()}>{hashCode}</span>
				</Title>
			</header>
			<figure className={figure()}>
				<Image
					src={destination.heroImage.url}
					width={1024}
					height={241}
					alt='travel info'
					className={image()}
					priority
				/>
			</figure>
			<InfoContainer asChild>
				<div>
					<ul className={list()}>
						<li>
							<InfoName>Vacation type / Destination</InfoName>
							<Info className={`${info()} space-x-2 gap-0`}>
								<IconUse id={getIcon(themes.slug)} width={20} height={20} />
								<span>
									{themes.title} – {destination.title}
								</span>
							</Info>
						</li>
						{themes.slug === 'cruises' && (
							<li>
								<InfoName>Ship</InfoName>
								<Info className={info()}>{type === 'quote' ? ships?.[0].name : ship?.name}</Info>
							</li>
						)}
						<li>
							<InfoName>{themes.slug === 'cruises' ? 'Embarkation Date' : 'Arrival Date'}</InfoName>
							<Info className={info()}>
								{type === 'quote'
									? `${arrivalDateQuote}`
									: convertDateFormatAndTz(initialDate as unknown as string, 'MM/DD/YYYY - hh:mm a')}
							</Info>
						</li>

						{!quoteAndCruise ? (
							<li>
								<InfoDate
									label={themes.slug === 'cruises' ? 'Disembarkation Date' : 'Departure Date'}
									date={
										type === 'quote' && themes.slug !== 'cruises'
											? `${departingDateQuote}`
											: convertDateFormatAndTz(finalDate as unknown as string, 'MM/DD/YYYY - hh:mm a')
									}
								/>
							</li>
						) : null}

						{(resortDays || parkDays) && (
							<li>
								<InfoName>Organization of Travel Days</InfoName>
								<Info className={info()}>
									{resortDays && parkDays ? (
										<>
											{parkDays ? `${parkDays} Park Days` : ''}, {resortDays ? `${resortDays} Resort Days` : ''} only
										</>
									) : (
										<>
											{parkDays || resortDays
												? `${parkDays || resortDays} ${parkDays ? 'Park Days' : 'Resort Days'}`
												: ''}{' '}
											only
										</>
									)}
								</Info>
							</li>
						)}

						{resortCategory && (
							<li>
								<InfoName>Resort Category</InfoName>
								<Info className={info()}>{resortCategory as string}</Info>
							</li>
						)}
						{resortPreference && (
							<li>
								<InfoName>Resort Preference</InfoName>
								<Info className={info()}>{resortPreference as string}</Info>
							</li>
						)}
						{membersWithAnnualPass && (
							<li>
								<InfoName>Annual Passes</InfoName>
								<Info className={info()}>{membersWithAnnualPass} passes</Info>
							</li>
						)}
						{partnerConfirmation && (
							<li>
								<InfoName>{destination.title} Confirmation</InfoName>
								<Info className={info()}>{partnerConfirmation}</Info>
							</li>
						)}
						{famviaConfirmation && (
							<li>
								<InfoName>Famvia Travel Confirmation</InfoName>
								<Info className={info()}>{famviaConfirmation}</Info>
							</li>
						)}
					</ul>
					{type !== 'quote' && (
						<>
							<div className={divider()}></div>
							<div className={footer()}>
								<div className={costWrapper()}>
									<div className={costWrapperInner()}>
										<span className={costLabel()}>Cost</span>
										<span className={cost()}>
											{investment ? investment.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : null}
										</span>
									</div>
									{trip.rewards && trip.rewards !== 0 ? (
										<div className='rounded bg-gray-700 px-4 py-1 text-primary-light md:ml-6 max-md:mt-5 flex items-center'>
											<IconUse id='rewards' width={16} height={16} fill='transparent' />
											<span className='font-semibold text-sm ml-1.5'>Rewards</span>
											<span className='font-bold ml-4 text-lg'>
												+{trip.rewards.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
											</span>
										</div>
									) : null}
								</div>
								<div className={`${divider()} md:hidden`}></div>
								<ViewPDF />
							</div>
						</>
					)}
				</div>
			</InfoContainer>
		</article>
	)
}
